import { c as _c } from "react/compiler-runtime";
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { isEqual } from 'lodash';
import { useDebounceValue } from '@/hooks/use-debounce';
import { FilterGroup, FirstInteractionFilterConfig, LastInteractionFilterConfig, LastInteractionTagFilterConfig, QueryOrdering, UsersInteractionsFilterConfig, UserConversationsCountFilterConfig, UserConversationLengthFilterConfig, ActivePeriodsFilterConfig, AvgSessionLengthFilterConfig, AboveValueConfigKind, InTheLastValueConfigKind } from '@/api/generated/model';
import { SAVE_FILTERS_REQUIRES_WRITE_MESSAGE, buildFilterKindName, getVariable, sortFilterKindsWithFavoritesFirst, useGetFilterValues } from '@/comps/FilterPicker/utils';
import { useAuthContext } from '@/hooks/use-auth';
import { useAliasedKindName } from '@/contexts/alias';
import { FILTERS, isFilterDefined } from '@/models/common/filters';
import { formatDateConfigs, formatValueConfigs, isSpecialFilter } from '@/utils/filter-utils';
import { CheckedState } from '@radix-ui/react-checkbox';
import { translate } from '@/utils/translations';
import { buildGenericFilter, filterToOperator, FilterOperator, getFilterValueDisplayText, isContainsFilter, supportsOperatorSelection } from '@/comps/FilterPicker/filter-operators';
import { AvailableValue, FilterButtonProps, GenericFilter, GenericUserFilter } from '@/comps/FilterPicker/FilterPicker';
import { useGlobalFiltersContext } from '@/contexts/filters/filter-context';
import { humanizeString } from '@/utils/string.utils';
import { useProjectsContext } from '@/providers/projects-provider';
import { useDialogs } from '@/contexts/dialogs/hooks';
import { toast } from 'sonner';
import { count, useLiveQuery } from '@tanstack/react-db';
import { favoriteFiltersCollection, favoriteSavedFiltersCollection, filterUsageCollection } from '@/comps/FilterPicker/favorites';
import { FilterContentV2Panel } from '@/comps/FilterPicker/filter-content-v2/FilterContentV2Panel';
import { FilterContentV2Row } from '@/comps/FilterPicker/filter-content-v2/FilterContentV2Row';
import { useFilterSelectAllV2 } from '@/comps/FilterPicker/filter-content-v2/useFilterSelectAllV2';
import { SaveFiltersDialog } from '@/comps/FilterPicker/FilterPickerHeader';
import { DeleteFilterGroupDialog } from '@/comps/FilterPicker/FilterContent';
import { filterKindsByCategory } from '@/comps/FilterPicker/FilterSidebar';
import { cn } from '@/utils/shad';
import { FilterContentV2FieldType, savedFilterGroupKey } from '@/comps/FilterPicker/filter-content-v2/FilterContentV2FieldDropdown';
import { createDefaultSpecialTimeFilterValues, createSpecialTimeStateForKind, formatSpecialTimePillSummary, getSpecialTimeKindFromValues, isInteractionTimeFilter, parseSpecialTimeFilterValues, serializeSpecialTimeFilterState, SpecialTimeFilterState, SpecialTimeKind, SpecialTimeFilterValues } from '@/comps/FilterPicker/filter-content-v2/specialTimeFilterAdapter';
import { createDefaultInteractionSpecialFilterValues, createInteractionSpecialStateForComparator, formatInteractionSpecialPillSummary, getInteractionSpecialComparatorFromValues, InteractionSpecialFilterState, InteractionSpecialFilterValues, isInteractionSpecialFilter, parseInteractionSpecialFilterValues, serializeInteractionSpecialFilterState, ValueComparatorKind } from '@/comps/FilterPicker/filter-content-v2/interactionSpecialFilterAdapter';
import { formatLastInteractionTagPillSummary, isLastInteractionTagFilter, LastInteractionTagFilterState, parseLastInteractionTagFilter, serializeLastInteractionTagFilter } from '@/comps/FilterPicker/filter-content-v2/lastInteractionTagFilterAdapter';
import { createDefaultUserMetricSpecialFilterValues, createUserMetricSpecialStateForComparator, formatUserMetricSpecialPillSummary, getUserMetricComparatorFromValues, isUserMetricSpecialFilter, parseUserMetricSpecialFilterValues, serializeUserMetricSpecialFilterState, UserMetricSpecialFilterState, UserMetricSpecialFilterValues } from '@/comps/FilterPicker/filter-content-v2/userMetricSpecialFilterAdapter';
type BuilderPopover = 'field' | 'operator' | 'value' | null;
type PendingDraftRowState = {
  filterKind: string | null;
  operator: FilterOperator | null;
  containsText: string;
  specialTimeKind: SpecialTimeKind | null;
  interactionComparator: ValueComparatorKind | null;
};
const FIELD_TYPE_OPTIONS: FilterContentV2FieldType[] = ['all', 'interactions', 'conversations', 'users', 'user_groups', 'tags', 'custom_columns', 'conversation_custom_columns', 'custom_aggregates', 'saved_filters', 'favorites', 'recently_used'];
const filterTextValue = (filter: GenericUserFilter) => {
  if (filter.column === FILTERS.firstInteraction) {
    const castFilter = filter as FirstInteractionFilterConfig;
    return `users whose first interaction is ${formatDateConfigs(castFilter.values)}`;
  }
  if (filter.column === FILTERS.lastInteraction) {
    const castFilter = filter as LastInteractionFilterConfig;
    return `users whose last interaction is ${formatDateConfigs(castFilter.values)}`;
  }
  if (filter.column === FILTERS.usersInteraction) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const castFilter = filter as any as UsersInteractionsFilterConfig;
    return `users who have ${formatValueConfigs(castFilter.values.interactions_count)} ${castFilter.values.dates ? formatDateConfigs(castFilter.values.dates) : ''}`;
  }
  if (filter.column === FILTERS.userConversationsCount) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const castFilter = filter as any as UserConversationsCountFilterConfig;
    return `users who have ${formatValueConfigs(castFilter.values.conversations_count)} conversations`;
  }
  if (filter.column === FILTERS.userConversationLength) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const castFilter = filter as any as UserConversationLengthFilterConfig;
    return `users who have had conversations with ${formatValueConfigs(castFilter.values.interactions_count)} interactions`;
  }
  if (filter.column === FILTERS.activePeriods) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const castFilter = filter as any as ActivePeriodsFilterConfig;
    const granularity = castFilter.values.granularity ?? 'week';
    return `users with ${formatValueConfigs(castFilter.values.active_count)} active periods of the last ${castFilter.values.window} ${granularity}s`;
  }
  if (filter.column === FILTERS.avgSessionLength) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const castFilter = filter as any as AvgSessionLengthFilterConfig;
    const durationText = formatValueConfigs(castFilter.values.duration);
    return castFilter.values.dates ? `users with avg session length ${durationText} ${formatDateConfigs(castFilter.values.dates)}` : `users with avg session length ${durationText}`;
  }
  if (filter.column === FILTERS.lastInteractionTag) {
    const castFilter = filter as LastInteractionTagFilterConfig;
    const search = castFilter.search?.trim();
    if (search && !castFilter.values?.length && !castFilter.select_all) {
      return `users whose last interaction tag "${castFilter.tag}" contains "${search}"`;
    }
    if (castFilter.select_all) {
      return `users whose last interaction tag "${castFilter.tag}" is All`;
    }
    const valuesText = castFilter.values?.length ? castFilter.values.join(', ') : '';
    return `users whose last interaction tag "${castFilter.tag}" is ${valuesText}`;
  }
  if (filter.column === FILTERS.activePeriods) {
    return formatUserMetricSpecialPillSummary(filter.column, filter.values) ?? '';
  }
  if (filter.column === FILTERS.avgSessionLength) {
    return formatUserMetricSpecialPillSummary(filter.column, filter.values) ?? '';
  }
  return '';
};
type SelectableOnlyProps = {
  open: boolean;
  setOpen: (v: boolean) => void;
  filterKind: string | null;
  setFilterKind: (v: string | null) => void;
  isPopover?: boolean;
  search: string;
  setSearch: (v: string) => void;
  filterSearch?: string;
  hideSaveButton?: boolean;
  hideFavoritesAndRecentlyUsedTabs?: boolean;
  hideSavedFilters?: boolean;
  isGlobal?: boolean;
  className?: string;
  /** Portal target for nested field/operator/value menus (e.g. Dialog content). */
  popoverContainer?: Element | null;
  /** Commit filter edits immediately and hide the Apply button. */
  autoApply?: boolean;
  /** Restrict which operators appear in the operator dropdown (default: all). */
  allowedOperators?: FilterOperator[];
  /** Filter columns that should show a validation error on the value pill. */
  invalidFilterColumns?: string[];
};
export const FilterContentV2 = props => {
  const $ = _c(365);
  const {
    filters: committedFilters,
    setFilters: commitFilters,
    filterKind,
    setFilterKind,
    timeRange,
    additionalFilters,
    volumeKind,
    setOpen,
    open,
    isPopover: t0,
    search: searchFilterValue,
    setSearch: setSearchFilterValue,
    filterSearch,
    onChecked,
    availableFilterKinds,
    filterListPrepend,
    showAdditionalFilterKinds,
    overrideSelectedFilters,
    ignoreFilters,
    hideSaveButton: t1,
    hideFavoritesAndRecentlyUsedTabs: t2,
    hideSavedFilters: t3,
    isGlobal: t4,
    className,
    showDraftActions,
    draftActions,
    popoverContainer,
    autoApply: t5,
    allowedOperators,
    invalidFilterColumns
  } = props;
  const isPopover = t0 === undefined ? false : t0;
  const hideSaveButton = t1 === undefined ? false : t1;
  const hideFavoritesAndRecentlyUsedTabs = t2 === undefined ? false : t2;
  const hideSavedFilters = t3 === undefined ? false : t3;
  const isGlobal = t4 === undefined ? false : t4;
  const autoApply = t5 === undefined ? false : t5;
  const {
    selectedProject,
    savedFilters,
    projectId: contextProjectId
  } = useProjectsContext();
  const {
    canWrite
  } = useAuthContext();
  const projectId = selectedProject?.id;
  const mappedProjectId = selectedProject?.mappedProjectId;
  const {
    aggregateBy
  } = useGlobalFiltersContext();
  const dialogs = useDialogs();
  const aliasKindName = useAliasedKindName();
  const [draftFilters, setDraftFilters] = useState(committedFilters);
  const [isDraftDirty, setIsDraftDirty] = useState(false);
  const prevOpenRef = useRef(open);
  const prevCommittedFiltersRef = useRef(committedFilters);
  const [filterSearchInternal, setFilterSearchInternal] = useState("");
  const [draftOperator, setDraftOperator] = useState(null);
  const [containsDraft, setContainsDraft] = useState("");
  const [activeFieldType, setActiveFieldType] = useState("all");
  const [builderPopoverRequest, setBuilderPopoverRequest] = useState();
  const [showDraftRow, setShowDraftRow] = useState(false);
  const [activeBuilderRow, setActiveBuilderRow] = useState("draft");
  const [draftFilterKind, setDraftFilterKind] = useState(null);
  const [pendingDraftRow, setPendingDraftRow] = useState(null);
  const [draftSpecialTimeKind, setDraftSpecialTimeKind] = useState(null);
  const [draftInteractionSpecialComparator, setDraftInteractionSpecialComparator] = useState(null);
  let t6;
  let t7;
  if ($[0] !== committedFilters || $[1] !== isDraftDirty) {
    t6 = () => {
      const committedChanged = !isEqual(prevCommittedFiltersRef.current, committedFilters);
      prevCommittedFiltersRef.current = committedFilters;
      if (isDraftDirty && !committedChanged) {
        return;
      }
      if (isDraftDirty) {
        setIsDraftDirty(false);
      }
      setDraftFilters(committedFilters);
    };
    t7 = [committedFilters, isDraftDirty];
    $[0] = committedFilters;
    $[1] = isDraftDirty;
    $[2] = t6;
    $[3] = t7;
  } else {
    t6 = $[2];
    t7 = $[3];
  }
  useEffect(t6, t7);
  let t8;
  let t9;
  if ($[4] !== committedFilters || $[5] !== isDraftDirty || $[6] !== open) {
    t8 = () => {
      const wasOpen = prevOpenRef.current;
      prevOpenRef.current = open;
      if (!wasOpen || open) {
        return;
      }
      if (isDraftDirty) {
        setIsDraftDirty(false);
        setDraftFilters(committedFilters);
      }
      setActiveBuilderRow("draft");
      setDraftFilterKind(null);
      setPendingDraftRow(null);
      setShowDraftRow(false);
      setDraftOperator(null);
      setContainsDraft("");
      setDraftSpecialTimeKind(null);
      setDraftInteractionSpecialComparator(null);
      setBuilderPopoverRequest(undefined);
    };
    t9 = [committedFilters, isDraftDirty, open];
    $[4] = committedFilters;
    $[5] = isDraftDirty;
    $[6] = open;
    $[7] = t8;
    $[8] = t9;
  } else {
    t8 = $[7];
    t9 = $[8];
  }
  useEffect(t8, t9);
  const filters = draftFilters;
  let t10;
  if ($[9] !== autoApply || $[10] !== commitFilters || $[11] !== committedFilters) {
    t10 = next => {
      if (autoApply) {
        setIsDraftDirty(false);
        setDraftFilters(next);
        commitFilters(next);
        return;
      }
      setIsDraftDirty(!isEqual(next, committedFilters));
      setDraftFilters(next);
    };
    $[9] = autoApply;
    $[10] = commitFilters;
    $[11] = committedFilters;
    $[12] = t10;
  } else {
    t10 = $[12];
  }
  const setFilters = t10;
  const filterSearchFinal = filterSearch || filterSearchInternal;
  const debouncedSearchFilterValue = useDebounceValue(searchFilterValue);
  const {
    data: favoriteFilters
  } = useLiveQuery(_temp);
  const {
    data: favoriteSavedFilters
  } = useLiveQuery(_temp2);
  const {
    data: mostUsedFilters
  } = useLiveQuery(_temp6);
  let t11;
  if ($[13] !== filterKind || $[14] !== filters) {
    let t12;
    if ($[16] !== filterKind) {
      t12 = f => f.column === filterKind;
      $[16] = filterKind;
      $[17] = t12;
    } else {
      t12 = $[17];
    }
    t11 = filters.find(t12);
    $[13] = filterKind;
    $[14] = filters;
    $[15] = t11;
  } else {
    t11 = $[15];
  }
  const targetFilter = t11;
  let t12;
  if ($[18] !== targetFilter?.values) {
    t12 = () => targetFilter?.values ?? [];
    $[18] = targetFilter?.values;
    $[19] = t12;
  } else {
    t12 = $[19];
  }
  targetFilter?.values;
  let t13;
  if ($[20] !== t12) {
    t13 = t12();
    $[20] = t12;
    $[21] = t13;
  } else {
    t13 = $[21];
  }
  const targetFilterValues = t13;
  let t14;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = {
      by: getVariable(),
      direction: "desc"
    };
    $[22] = t14;
  } else {
    t14 = $[22];
  }
  const [sortBy, setSortBy] = useState(t14);
  let t15;
  if ($[23] !== additionalFilters || $[24] !== aliasKindName || $[25] !== availableFilterKinds || $[26] !== filterSearchFinal || $[27] !== showAdditionalFilterKinds) {
    let t16;
    if ($[29] !== additionalFilters || $[30] !== showAdditionalFilterKinds) {
      t16 = kind => !additionalFilters?.find(f_0 => f_0.column === kind) || showAdditionalFilterKinds;
      $[29] = additionalFilters;
      $[30] = showAdditionalFilterKinds;
      $[31] = t16;
    } else {
      t16 = $[31];
    }
    let t17;
    if ($[32] !== aliasKindName || $[33] !== filterSearchFinal) {
      t17 = kind_0 => buildFilterKindName(kind_0, aliasKindName).toLowerCase().includes(filterSearchFinal);
      $[32] = aliasKindName;
      $[33] = filterSearchFinal;
      $[34] = t17;
    } else {
      t17 = $[34];
    }
    t15 = availableFilterKinds.filter(t16).filter(t17);
    $[23] = additionalFilters;
    $[24] = aliasKindName;
    $[25] = availableFilterKinds;
    $[26] = filterSearchFinal;
    $[27] = showAdditionalFilterKinds;
    $[28] = t15;
  } else {
    t15 = $[28];
  }
  const filteredAvailableFilterKindsNoCategory = t15;
  let t16;
  bb0: {
    if (activeFieldType === "recently_used") {
      let t17;
      if ($[35] !== filteredAvailableFilterKindsNoCategory || $[36] !== mostUsedFilters) {
        const recentKinds = (mostUsedFilters ?? []).map(_temp7);
        let t18;
        if ($[38] !== filteredAvailableFilterKindsNoCategory) {
          t18 = kind_1 => filteredAvailableFilterKindsNoCategory.includes(kind_1);
          $[38] = filteredAvailableFilterKindsNoCategory;
          $[39] = t18;
        } else {
          t18 = $[39];
        }
        t17 = recentKinds.filter(t18);
        $[35] = filteredAvailableFilterKindsNoCategory;
        $[36] = mostUsedFilters;
        $[37] = t17;
      } else {
        t17 = $[37];
      }
      t16 = t17;
      break bb0;
    }
    if (activeFieldType === "favorites") {
      let t17;
      if ($[40] !== favoriteFilters || $[41] !== filteredAvailableFilterKindsNoCategory) {
        const favoriteKinds = (favoriteFilters ?? []).map(_temp8);
        let t18;
        if ($[43] !== filteredAvailableFilterKindsNoCategory) {
          t18 = kind_2 => filteredAvailableFilterKindsNoCategory.includes(kind_2);
          $[43] = filteredAvailableFilterKindsNoCategory;
          $[44] = t18;
        } else {
          t18 = $[44];
        }
        t17 = favoriteKinds.filter(t18);
        $[40] = favoriteFilters;
        $[41] = filteredAvailableFilterKindsNoCategory;
        $[42] = t17;
      } else {
        t17 = $[42];
      }
      t16 = t17;
      break bb0;
    }
    if (activeFieldType === "saved_filters") {
      let t17;
      if ($[45] === Symbol.for("react.memo_cache_sentinel")) {
        t17 = [];
        $[45] = t17;
      } else {
        t17 = $[45];
      }
      t16 = t17;
      break bb0;
    }
    let t17;
    if ($[46] !== activeFieldType || $[47] !== filteredAvailableFilterKindsNoCategory) {
      t17 = filterKindsByCategory(filteredAvailableFilterKindsNoCategory, activeFieldType);
      $[46] = activeFieldType;
      $[47] = filteredAvailableFilterKindsNoCategory;
      $[48] = t17;
    } else {
      t17 = $[48];
    }
    t16 = t17;
  }
  const filteredAvailableFilterKinds = t16;
  let t17;
  if ($[49] !== filteredAvailableFilterKindsNoCategory || $[50] !== hideFavoritesAndRecentlyUsedTabs || $[51] !== hideSavedFilters || $[52] !== savedFilters.length) {
    t17 = FIELD_TYPE_OPTIONS.filter(type => {
      if (type === "saved_filters") {
        return savedFilters.length > 0 && !hideSavedFilters;
      }
      if (type === "favorites" || type === "recently_used") {
        return !hideFavoritesAndRecentlyUsedTabs;
      }
      if (type === "all") {
        return true;
      }
      return filterKindsByCategory(filteredAvailableFilterKindsNoCategory, type).length > 0;
    });
    $[49] = filteredAvailableFilterKindsNoCategory;
    $[50] = hideFavoritesAndRecentlyUsedTabs;
    $[51] = hideSavedFilters;
    $[52] = savedFilters.length;
    $[53] = t17;
  } else {
    t17 = $[53];
  }
  const availableFieldTypes = t17;
  let t18;
  let t19;
  if ($[54] !== activeFieldType || $[55] !== availableFieldTypes) {
    t18 = () => {
      if (!availableFieldTypes.includes(activeFieldType)) {
        setActiveFieldType("all");
      }
    };
    t19 = [activeFieldType, availableFieldTypes];
    $[54] = activeFieldType;
    $[55] = availableFieldTypes;
    $[56] = t18;
    $[57] = t19;
  } else {
    t18 = $[56];
    t19 = $[57];
  }
  useEffect(t18, t19);
  const t20 = overrideSelectedFilters ?? filters;
  let t21;
  if ($[58] !== t20) {
    t21 = t20.reduce(_temp9, {} as Record<string, string[]>);
    $[58] = t20;
    $[59] = t21;
  } else {
    t21 = $[59];
  }
  const filtersMapByKind = t21;
  let t22;
  if ($[60] !== draftOperator || $[61] !== filterKind) {
    t22 = !!filterKind && draftOperator !== "contains" && supportsOperatorSelection(filterKind) && !isSpecialFilter(filterKind);
    $[60] = draftOperator;
    $[61] = filterKind;
    $[62] = t22;
  } else {
    t22 = $[62];
  }
  const valuesFetchEnabled = t22;
  const t23 = isGlobal ? mappedProjectId ?? projectId : projectId;
  const t24 = targetFilter?.select_all;
  let t25;
  if ($[63] !== additionalFilters || $[64] !== aggregateBy || $[65] !== debouncedSearchFilterValue || $[66] !== filterKind || $[67] !== filters || $[68] !== filtersMapByKind || $[69] !== props.filters_v2 || $[70] !== sortBy || $[71] !== t23 || $[72] !== t24 || $[73] !== timeRange || $[74] !== valuesFetchEnabled || $[75] !== volumeKind) {
    t25 = {
      filters,
      filters_v2: props.filters_v2,
      filterKind,
      timeRange,
      projectId: t23,
      search: debouncedSearchFilterValue,
      additionalFilters,
      filtersMapByKind,
      isSelectedAll: t24,
      volumeKind,
      sortBy,
      aggregateBy,
      enabled: valuesFetchEnabled
    };
    $[63] = additionalFilters;
    $[64] = aggregateBy;
    $[65] = debouncedSearchFilterValue;
    $[66] = filterKind;
    $[67] = filters;
    $[68] = filtersMapByKind;
    $[69] = props.filters_v2;
    $[70] = sortBy;
    $[71] = t23;
    $[72] = t24;
    $[73] = timeRange;
    $[74] = valuesFetchEnabled;
    $[75] = volumeKind;
    $[76] = t25;
  } else {
    t25 = $[76];
  }
  const {
    availableFilterValues: tempAvailableFilterValues,
    isLoading
  } = useGetFilterValues(t25);
  let t26;
  if ($[77] !== filterKind || $[78] !== ignoreFilters || $[79] !== tempAvailableFilterValues) {
    bb1: {
      if (!ignoreFilters) {
        t26 = tempAvailableFilterValues;
        break bb1;
      }
      let t27;
      if ($[81] !== filterKind) {
        t27 = f_1 => f_1.column === filterKind;
        $[81] = filterKind;
        $[82] = t27;
      } else {
        t27 = $[82];
      }
      const ignoreFilter = ignoreFilters.find(t27);
      if (!ignoreFilter) {
        t26 = tempAvailableFilterValues;
        break bb1;
      }
      t26 = tempAvailableFilterValues?.filter(av => !ignoreFilter.values.includes(av.value));
    }
    $[77] = filterKind;
    $[78] = ignoreFilters;
    $[79] = tempAvailableFilterValues;
    $[80] = t26;
  } else {
    t26 = $[80];
  }
  const availableFilterValues = t26;
  const showSelectAll = (availableFilterValues?.length ?? 0) > 1;
  const draftIsExclude = draftOperator === "is_not";
  let t27;
  if ($[83] !== availableFilterValues) {
    t27 = availableFilterValues?.map(_temp10) ?? [];
    $[83] = availableFilterValues;
    $[84] = t27;
  } else {
    t27 = $[84];
  }
  let t28;
  if ($[85] !== draftIsExclude || $[86] !== filterKind || $[87] !== filters || $[88] !== setFilters || $[89] !== t27) {
    t28 = {
      filters,
      filterKind,
      availableFilterValues: t27,
      setFilters,
      isExclude: draftIsExclude
    };
    $[85] = draftIsExclude;
    $[86] = filterKind;
    $[87] = filters;
    $[88] = setFilters;
    $[89] = t27;
    $[90] = t28;
  } else {
    t28 = $[90];
  }
  const {
    handleOnCheckSelectAll,
    hasSelectedAll,
    allValuesSnapshot
  } = useFilterSelectAllV2(t28);
  let t29;
  if ($[91] === Symbol.for("react.memo_cache_sentinel")) {
    t29 = () => {
      setDraftOperator(null);
      setContainsDraft("");
      setDraftSpecialTimeKind(null);
      setDraftInteractionSpecialComparator(null);
    };
    $[91] = t29;
  } else {
    t29 = $[91];
  }
  const resetBuilderDraft = t29;
  let t30;
  bb2: {
    if (!showDraftRow) {
      t30 = false;
      break bb2;
    }
    const kind_3 = pendingDraftRow?.filterKind ?? draftFilterKind;
    if (!kind_3) {
      t30 = true;
      break bb2;
    }
    let t31;
    if ($[92] !== filters || $[93] !== kind_3) {
      let t32;
      if ($[95] !== kind_3) {
        t32 = filter_2 => filter_2.column === kind_3 && isFilterDefined(filter_2 as GenericFilter);
        $[95] = kind_3;
        $[96] = t32;
      } else {
        t32 = $[96];
      }
      t31 = filters.some(t32);
      $[92] = filters;
      $[93] = kind_3;
      $[94] = t31;
    } else {
      t31 = $[94];
    }
    t30 = !t31;
  }
  const hasUncommittedDraft = t30;
  let t31;
  if ($[97] !== containsDraft || $[98] !== draftFilterKind || $[99] !== draftInteractionSpecialComparator || $[100] !== draftOperator || $[101] !== draftSpecialTimeKind || $[102] !== filterKind) {
    t31 = () => {
      setPendingDraftRow({
        filterKind: draftFilterKind ?? filterKind,
        operator: draftOperator,
        containsText: containsDraft,
        specialTimeKind: draftSpecialTimeKind,
        interactionComparator: draftInteractionSpecialComparator
      });
    };
    $[97] = containsDraft;
    $[98] = draftFilterKind;
    $[99] = draftInteractionSpecialComparator;
    $[100] = draftOperator;
    $[101] = draftSpecialTimeKind;
    $[102] = filterKind;
    $[103] = t31;
  } else {
    t31 = $[103];
  }
  const snapshotDraftRow = t31;
  let t32;
  if ($[104] !== draftFilterKind || $[105] !== pendingDraftRow || $[106] !== setFilterKind) {
    t32 = () => {
      setActiveBuilderRow("draft");
      if (pendingDraftRow) {
        setDraftFilterKind(pendingDraftRow.filterKind);
        setFilterKind(pendingDraftRow.filterKind);
        setDraftOperator(pendingDraftRow.operator);
        setContainsDraft(pendingDraftRow.containsText);
        setDraftSpecialTimeKind(pendingDraftRow.specialTimeKind);
        setDraftInteractionSpecialComparator(pendingDraftRow.interactionComparator);
        return;
      }
      setFilterKind(draftFilterKind);
    };
    $[104] = draftFilterKind;
    $[105] = pendingDraftRow;
    $[106] = setFilterKind;
    $[107] = t32;
  } else {
    t32 = $[107];
  }
  const activateDraftBuilderRow = t32;
  let t33;
  if ($[108] !== activeBuilderRow || $[109] !== hasUncommittedDraft || $[110] !== setFilterKind || $[111] !== showDraftRow || $[112] !== snapshotDraftRow) {
    t33 = rowFilter => {
      if (activeBuilderRow === rowFilter.column) {
        return;
      }
      if (showDraftRow && activeBuilderRow === "draft" && hasUncommittedDraft) {
        snapshotDraftRow();
      }
      setActiveBuilderRow(rowFilter.column);
      setFilterKind(rowFilter.column);
      if (!hasUncommittedDraft) {
        setShowDraftRow(false);
      }
      setDraftOperator(filterToOperator(rowFilter));
      setContainsDraft(isContainsFilter(rowFilter) ? rowFilter.search ?? "" : "");
      if (isInteractionTimeFilter(rowFilter.column)) {
        setDraftSpecialTimeKind(getSpecialTimeKindFromValues(rowFilter.values) ?? InTheLastValueConfigKind.in_the_last);
      }
      if (isInteractionSpecialFilter(rowFilter.column)) {
        setDraftInteractionSpecialComparator(getInteractionSpecialComparatorFromValues(rowFilter.column, rowFilter.values) ?? AboveValueConfigKind.above);
      } else {
        if (isUserMetricSpecialFilter(rowFilter.column)) {
          setDraftInteractionSpecialComparator(getUserMetricComparatorFromValues(rowFilter.column, rowFilter.values) ?? AboveValueConfigKind.above);
        }
      }
    };
    $[108] = activeBuilderRow;
    $[109] = hasUncommittedDraft;
    $[110] = setFilterKind;
    $[111] = showDraftRow;
    $[112] = snapshotDraftRow;
    $[113] = t33;
  } else {
    t33 = $[113];
  }
  const activateAppliedBuilderRow = t33;
  let t34;
  if ($[114] === Symbol.for("react.memo_cache_sentinel")) {
    t34 = target => {
      setBuilderPopoverRequest(prev => ({
        target,
        nonce: (prev?.nonce ?? 0) + 1
      }));
    };
    $[114] = t34;
  } else {
    t34 = $[114];
  }
  const requestBuilderPopover = t34;
  let t35;
  if ($[115] === Symbol.for("react.memo_cache_sentinel")) {
    t35 = () => {
      setBuilderPopoverRequest(undefined);
    };
    $[115] = t35;
  } else {
    t35 = $[115];
  }
  const clearBuilderPopoverRequest = t35;
  let t36;
  if ($[116] !== filters) {
    t36 = (column, nextFilter) => {
      let replaced = false;
      const nextFilters = filters.flatMap(filter_3 => {
        if (filter_3.column !== column) {
          return [filter_3];
        }
        replaced = true;
        return nextFilter ? [nextFilter] : [];
      });
      if (!replaced && nextFilter) {
        nextFilters.push(nextFilter);
      }
      return nextFilters;
    };
    $[116] = filters;
    $[117] = t36;
  } else {
    t36 = $[117];
  }
  const replaceFilterPreservingOrder = t36;
  let t37;
  if ($[118] !== filters) {
    t37 = (column_0, values, existingFilter) => {
      if (existingFilter) {
        return {
          ...existingFilter,
          values
        } as T;
      }
      const persistedFilter = filters.find(filter_4 => filter_4.column === column_0);
      if (persistedFilter) {
        return {
          ...persistedFilter,
          values
        } as T;
      }
      return {
        column: column_0,
        values
      } as GenericUserFilter as T;
    };
    $[118] = filters;
    $[119] = t37;
  } else {
    t37 = $[119];
  }
  const toSpecialTimeFilter = t37;
  let t38;
  if ($[120] !== filters) {
    t38 = (column_1, values_0, existingFilter_0) => {
      if (existingFilter_0) {
        return {
          ...existingFilter_0,
          values: values_0
        } as T;
      }
      const persistedFilter_0 = filters.find(filter_5 => filter_5.column === column_1);
      if (persistedFilter_0) {
        return {
          ...persistedFilter_0,
          values: values_0
        } as T;
      }
      return {
        column: column_1,
        values: values_0
      } as unknown as T;
    };
    $[120] = filters;
    $[121] = t38;
  } else {
    t38 = $[121];
  }
  const toInteractionSpecialFilter = t38;
  let t39;
  if ($[122] !== filters) {
    t39 = (column_2, values_1, existingFilter_1) => {
      if (existingFilter_1) {
        return {
          ...existingFilter_1,
          values: values_1
        } as T;
      }
      const persistedFilter_1 = filters.find(filter_6 => filter_6.column === column_2);
      if (persistedFilter_1) {
        return {
          ...persistedFilter_1,
          values: values_1
        } as T;
      }
      return {
        column: column_2,
        values: values_1
      } as unknown as T;
    };
    $[122] = filters;
    $[123] = t39;
  } else {
    t39 = $[123];
  }
  const toUserMetricSpecialFilter = t39;
  let t40;
  if ($[124] !== activeBuilderRow || $[125] !== filters || $[126] !== hasUncommittedDraft || $[127] !== setFilterKind || $[128] !== snapshotDraftRow) {
    t40 = kind_4 => {
      setFilterSearchInternal("");
      const existingFilter_2 = filters.find(f_2 => f_2.column === kind_4);
      if (existingFilter_2) {
        if (hasUncommittedDraft && activeBuilderRow === "draft") {
          snapshotDraftRow();
        } else {
          setShowDraftRow(false);
        }
        setActiveBuilderRow(kind_4);
        setFilterKind(kind_4);
        if (isInteractionTimeFilter(kind_4)) {
          resetBuilderDraft();
          setDraftSpecialTimeKind(getSpecialTimeKindFromValues(existingFilter_2.values) ?? InTheLastValueConfigKind.in_the_last);
        } else {
          if (isInteractionSpecialFilter(kind_4)) {
            resetBuilderDraft();
            setDraftInteractionSpecialComparator(getInteractionSpecialComparatorFromValues(kind_4, existingFilter_2.values) ?? AboveValueConfigKind.above);
          } else {
            if (isUserMetricSpecialFilter(kind_4)) {
              resetBuilderDraft();
              setDraftInteractionSpecialComparator(getUserMetricComparatorFromValues(kind_4, existingFilter_2.values) ?? AboveValueConfigKind.above);
            } else {
              if (isLastInteractionTagFilter(kind_4)) {
                resetBuilderDraft();
                setDraftOperator(filterToOperator(existingFilter_2));
                setContainsDraft(isContainsFilter(existingFilter_2) ? existingFilter_2.search ?? "" : "");
              } else {
                setDraftOperator(filterToOperator(existingFilter_2));
                setContainsDraft(isContainsFilter(existingFilter_2) ? existingFilter_2.search ?? "" : "");
              }
            }
          }
        }
        requestBuilderPopover("operator");
        return;
      }
      setActiveBuilderRow("draft");
      setDraftFilterKind(kind_4);
      setFilterKind(kind_4);
      setShowDraftRow(true);
      setPendingDraftRow(null);
      if (isInteractionTimeFilter(kind_4)) {
        resetBuilderDraft();
        setDraftSpecialTimeKind(InTheLastValueConfigKind.in_the_last);
        requestBuilderPopover("operator");
        return;
      }
      if (isInteractionSpecialFilter(kind_4)) {
        resetBuilderDraft();
        setDraftInteractionSpecialComparator(AboveValueConfigKind.above);
        requestBuilderPopover("operator");
        return;
      }
      if (isUserMetricSpecialFilter(kind_4)) {
        resetBuilderDraft();
        setDraftInteractionSpecialComparator(AboveValueConfigKind.above);
        requestBuilderPopover("operator");
        return;
      }
      if (isLastInteractionTagFilter(kind_4)) {
        resetBuilderDraft();
        setDraftOperator("equals");
        requestBuilderPopover("operator");
        return;
      }
      resetBuilderDraft();
      setDraftOperator("equals");
    };
    $[124] = activeBuilderRow;
    $[125] = filters;
    $[126] = hasUncommittedDraft;
    $[127] = setFilterKind;
    $[128] = snapshotDraftRow;
    $[129] = t40;
  } else {
    t40 = $[129];
  }
  const handleSelectField = t40;
  let t41;
  if ($[130] !== filters || $[131] !== onChecked || $[132] !== setFilterKind || $[133] !== setFilters || $[134] !== setSearchFilterValue) {
    t41 = (rowFilter_0, kind_5) => {
      if (!isInteractionTimeFilter(kind_5) && !isInteractionSpecialFilter(kind_5) && !isUserMetricSpecialFilter(kind_5) && !isLastInteractionTagFilter(kind_5)) {
        return false;
      }
      const nextFilter_0 = isInteractionTimeFilter(kind_5) ? {
        column: kind_5,
        values: createDefaultSpecialTimeFilterValues()
      } as GenericUserFilter as T : isInteractionSpecialFilter(kind_5) ? {
        column: kind_5,
        values: createDefaultInteractionSpecialFilterValues(kind_5)
      } as unknown as T : isUserMetricSpecialFilter(kind_5) ? {
        column: kind_5,
        values: createDefaultUserMetricSpecialFilterValues(kind_5)
      } as unknown as T : {
        column: kind_5,
        values: []
      } as unknown as T;
      const nextFilters_0 = filters.flatMap(filter_7 => {
        if (filter_7.column === rowFilter_0.column) {
          return [nextFilter_0];
        }
        if (filter_7.column === kind_5) {
          return [];
        }
        return [filter_7];
      });
      resetBuilderDraft();
      if (isInteractionTimeFilter(kind_5)) {
        setDraftSpecialTimeKind(InTheLastValueConfigKind.in_the_last);
      } else {
        if (isInteractionSpecialFilter(kind_5)) {
          setDraftInteractionSpecialComparator(AboveValueConfigKind.above);
        } else {
          if (isUserMetricSpecialFilter(kind_5)) {
            setDraftInteractionSpecialComparator(AboveValueConfigKind.above);
          } else {
            setDraftOperator("equals");
            setContainsDraft("");
          }
        }
      }
      setActiveBuilderRow(kind_5);
      setFilterKind(kind_5);
      setShowDraftRow(false);
      setContainsDraft("");
      setSearchFilterValue("");
      setFilters(nextFilters_0);
      onChecked?.(false, kind_5, nextFilters_0);
      requestBuilderPopover("operator");
      return true;
    };
    $[130] = filters;
    $[131] = onChecked;
    $[132] = setFilterKind;
    $[133] = setFilters;
    $[134] = setSearchFilterValue;
    $[135] = t41;
  } else {
    t41 = $[135];
  }
  const replaceExistingRowWithSpecialFilter = t41;
  let t42;
  if ($[136] !== draftOperator || $[137] !== filterKind || $[138] !== filters || $[139] !== onChecked || $[140] !== replaceFilterPreservingOrder || $[141] !== setFilters || $[142] !== setSearchFilterValue) {
    t42 = (operator, existingFilter_3) => {
      const currentFilter = existingFilter_3 ?? (filterKind ? filters.find(f_3 => f_3.column === filterKind) as T | undefined : undefined);
      const appliedOperator = currentFilter ? filterToOperator(currentFilter) : null;
      const previousOperator = draftOperator ?? appliedOperator;
      const operatorChanged = previousOperator !== operator;
      const leavingContains = operatorChanged && operator !== "contains" && (currentFilter ? isContainsFilter(currentFilter) : false);
      setDraftOperator(operator);
      if (operator === "contains") {
        setContainsDraft(currentFilter?.search?.trim() ?? "");
        setSearchFilterValue("");
        return;
      }
      if (operatorChanged) {
        setContainsDraft("");
        setSearchFilterValue("");
      }
      const column_3 = existingFilter_3?.column ?? filterKind;
      if (!column_3) {
        return;
      }
      if (isLastInteractionTagFilter(column_3)) {
        if (!currentFilter) {
          return;
        }
        const tagFilter = currentFilter as GenericFilter & {
          tag?: string;
        };
        if (!tagFilter.tag) {
          return;
        }
        const nextConfig = serializeLastInteractionTagFilter({
          tag: tagFilter.tag,
          operator,
          values: Array.isArray(currentFilter.values) ? currentFilter.values : [],
          select_all: currentFilter.select_all,
          search: undefined
        });
        if (!nextConfig) {
          return;
        }
        const nextFilter_1 = {
          ...currentFilter,
          ...nextConfig
        } as T;
        const nextFilters_1 = replaceFilterPreservingOrder(column_3, nextFilter_1);
        setFilters(nextFilters_1);
        onChecked?.(false, column_3, nextFilters_1);
        return;
      }
      if (leavingContains) {
        if (!currentFilter || !isFilterDefined(currentFilter as GenericFilter)) {
          return;
        }
        const nextFilter_2 = buildGenericFilter({
          column: column_3,
          operator,
          values: []
        }) as T;
        const nextFilters_2 = replaceFilterPreservingOrder(column_3, nextFilter_2);
        setFilters(nextFilters_2);
        onChecked?.(false, column_3, nextFilters_2);
        return;
      }
      if (!currentFilter) {
        return;
      }
      if (appliedOperator === operator) {
        return;
      }
      const values_2 = currentFilter.values ?? [];
      const canReuseExistingValues = values_2.length > 0 || !!currentFilter.select_all;
      if (!canReuseExistingValues) {
        return;
      }
      const nextFilter_3 = buildGenericFilter({
        column: column_3,
        operator,
        values: values_2,
        selectAll: currentFilter.select_all
      }) as T;
      const nextFilters_3 = replaceFilterPreservingOrder(column_3, nextFilter_3);
      setFilters(nextFilters_3);
      onChecked?.(false, column_3, nextFilters_3);
    };
    $[136] = draftOperator;
    $[137] = filterKind;
    $[138] = filters;
    $[139] = onChecked;
    $[140] = replaceFilterPreservingOrder;
    $[141] = setFilters;
    $[142] = setSearchFilterValue;
    $[143] = t42;
  } else {
    t42 = $[143];
  }
  const handleSelectOperator = t42;
  let t43;
  if ($[144] !== draftOperator || $[145] !== filterKind || $[146] !== onChecked || $[147] !== replaceFilterPreservingOrder || $[148] !== setFilters || $[149] !== targetFilter) {
    t43 = (values_3, checked, selectAllOverride) => {
      if (!filterKind) {
        return;
      }
      const operator_0 = draftOperator ?? (targetFilter ? filterToOperator(targetFilter) : "equals");
      let nextFilter_4 = null;
      if (values_3.length) {
        nextFilter_4 = buildGenericFilter({
          column: filterKind,
          operator: operator_0,
          values: values_3,
          selectAll: selectAllOverride ?? targetFilter?.select_all
        }) as T;
      }
      const nextFilters_4 = replaceFilterPreservingOrder(filterKind, nextFilter_4);
      setFilters(nextFilters_4);
      if (!nextFilter_4) {
        setActiveBuilderRow("draft");
        setShowDraftRow(true);
      }
      onChecked?.(checked, filterKind, nextFilters_4);
    };
    $[144] = draftOperator;
    $[145] = filterKind;
    $[146] = onChecked;
    $[147] = replaceFilterPreservingOrder;
    $[148] = setFilters;
    $[149] = targetFilter;
    $[150] = t43;
  } else {
    t43 = $[150];
  }
  const handleFilterChange = t43;
  let t44;
  if ($[151] !== setFilterKind || $[152] !== setSearchFilterValue) {
    t44 = () => {
      setActiveBuilderRow("draft");
      setDraftFilterKind(null);
      setPendingDraftRow(null);
      setFilterKind(null);
      setShowDraftRow(true);
      resetBuilderDraft();
      setSearchFilterValue("");
      setFilterSearchInternal("");
      requestBuilderPopover("field");
    };
    $[151] = setFilterKind;
    $[152] = setSearchFilterValue;
    $[153] = t44;
  } else {
    t44 = $[153];
  }
  const handleNewFilterRow = t44;
  let t45;
  if ($[154] !== setFilterKind || $[155] !== setFilters || $[156] !== setSearchFilterValue) {
    t45 = filterGroup => {
      const nextFilters_5 = filterGroup.filters.map(_temp11) as T[];
      setFilters(nextFilters_5);
      setActiveBuilderRow("draft");
      setDraftFilterKind(null);
      setPendingDraftRow(null);
      setFilterKind(null);
      setShowDraftRow(false);
      resetBuilderDraft();
      setSearchFilterValue("");
      setFilterSearchInternal("");
    };
    $[154] = setFilterKind;
    $[155] = setFilters;
    $[156] = setSearchFilterValue;
    $[157] = t45;
  } else {
    t45 = $[157];
  }
  const handleSelectSavedFilterGroup = t45;
  let t46;
  if ($[158] !== setFilterKind || $[159] !== setSearchFilterValue) {
    t46 = t47 => {
      const showDraft = t47 === undefined ? true : t47;
      setActiveBuilderRow("draft");
      setDraftFilterKind(null);
      setPendingDraftRow(null);
      setFilterKind(null);
      setShowDraftRow(showDraft);
      resetBuilderDraft();
      setSearchFilterValue("");
      setFilterSearchInternal("");
    };
    $[158] = setFilterKind;
    $[159] = setSearchFilterValue;
    $[160] = t46;
  } else {
    t46 = $[160];
  }
  const handleResetBuilderToDraft = t46;
  let t47;
  if ($[161] !== activeBuilderRow || $[162] !== filterKind || $[163] !== filters || $[164] !== handleResetBuilderToDraft || $[165] !== onChecked || $[166] !== setFilters) {
    t47 = rowFilter_1 => {
      const nextFilters_6 = filters.filter(f_4 => f_4.column !== rowFilter_1.column);
      setFilters(nextFilters_6);
      onChecked?.(false, rowFilter_1.column, nextFilters_6);
      if (!nextFilters_6.length) {
        handleResetBuilderToDraft(true);
      } else {
        if (filterKind === rowFilter_1.column || activeBuilderRow === rowFilter_1.column) {
          handleResetBuilderToDraft(false);
        }
      }
    };
    $[161] = activeBuilderRow;
    $[162] = filterKind;
    $[163] = filters;
    $[164] = handleResetBuilderToDraft;
    $[165] = onChecked;
    $[166] = setFilters;
    $[167] = t47;
  } else {
    t47 = $[167];
  }
  const handleRemoveAppliedFilterRow = t47;
  let t48;
  if ($[168] !== filterKind || $[169] !== filters || $[170] !== handleResetBuilderToDraft || $[171] !== onChecked || $[172] !== setFilters) {
    t48 = () => {
      setPendingDraftRow(null);
      setDraftFilterKind(null);
      if (filterKind && filters.some(f_6 => f_6.column === filterKind)) {
        const nextFilters_7 = filters.filter(f_5 => f_5.column !== filterKind);
        setFilters(nextFilters_7);
        onChecked?.(false, filterKind, nextFilters_7);
        handleResetBuilderToDraft(nextFilters_7.length === 0);
        return;
      }
      if (filters.length === 0) {
        handleResetBuilderToDraft(true);
        return;
      }
      handleResetBuilderToDraft(false);
    };
    $[168] = filterKind;
    $[169] = filters;
    $[170] = handleResetBuilderToDraft;
    $[171] = onChecked;
    $[172] = setFilters;
    $[173] = t48;
  } else {
    t48 = $[173];
  }
  const handleClearDraftRow = t48;
  let t49;
  if ($[174] !== contextProjectId || $[175] !== dialogs || $[176] !== filters || $[177] !== savedFilters) {
    t49 = () => {
      if (!contextProjectId) {
        toast.error("No project selected");
        return;
      }
      dialogs.push({
        content: <SaveFiltersDialog savedFilters={savedFilters} projectId={contextProjectId} filters={filters} />
      });
    };
    $[174] = contextProjectId;
    $[175] = dialogs;
    $[176] = filters;
    $[177] = savedFilters;
    $[178] = t49;
  } else {
    t49 = $[178];
  }
  const handleSaveFilters = t49;
  let t50;
  if ($[179] !== contextProjectId || $[180] !== dialogs) {
    t50 = filterGroup_0 => {
      if (!contextProjectId) {
        toast.error("No project selected");
        return;
      }
      dialogs.push({
        content: <DeleteFilterGroupDialog filterGroup={filterGroup_0} projectId={contextProjectId} />
      });
    };
    $[179] = contextProjectId;
    $[180] = dialogs;
    $[181] = t50;
  } else {
    t50 = $[181];
  }
  const handleDeleteSavedFilterGroup = t50;
  let t51;
  if ($[182] !== favoriteSavedFilters) {
    t51 = new Set(favoriteSavedFilters?.map(_temp12) ?? []);
    $[182] = favoriteSavedFilters;
    $[183] = t51;
  } else {
    t51 = $[183];
  }
  const favoriteSavedFilterGroupsSet = t51;
  const handleToggleFavoriteSavedFilterGroup = _temp13;
  let t52;
  if ($[184] !== favoriteFilters) {
    t52 = new Set(favoriteFilters?.map(_temp14) ?? []);
    $[184] = favoriteFilters;
    $[185] = t52;
  } else {
    t52 = $[185];
  }
  const favoriteTypesSet = t52;
  let t53;
  if ($[186] !== favoriteTypesSet) {
    t53 = kinds => sortFilterKindsWithFavoritesFirst(kinds, favoriteTypesSet);
    $[186] = favoriteTypesSet;
    $[187] = t53;
  } else {
    t53 = $[187];
  }
  const sortKindsWithFavoritesFirst = t53;
  let t54;
  if ($[188] !== activeBuilderRow || $[189] !== containsDraft || $[190] !== draftFilterKind || $[191] !== filterKind || $[192] !== filters || $[193] !== onChecked || $[194] !== replaceFilterPreservingOrder || $[195] !== setFilters || $[196] !== setSearchFilterValue) {
    t54 = (textOverride, columnOverride) => {
      const text = (textOverride ?? containsDraft).trim();
      const column_4 = columnOverride ?? (activeBuilderRow === "draft" ? draftFilterKind ?? filterKind : activeBuilderRow);
      if (!column_4) {
        return;
      }
      if (!text) {
        const existing = filters.find(f_9 => f_9.column === column_4);
        if (!existing || !isContainsFilter(existing)) {
          return;
        }
        const nextFilters_8 = replaceFilterPreservingOrder(column_4, null);
        setFilters(nextFilters_8);
        setActiveBuilderRow("draft");
        setShowDraftRow(true);
        onChecked?.(false, column_4, nextFilters_8);
        return;
      }
      const nextFilter_5 = buildGenericFilter({
        column: column_4,
        operator: "contains",
        values: [],
        containsText: text
      }) as T;
      const nextFilters_9 = replaceFilterPreservingOrder(column_4, nextFilter_5);
      setFilters(nextFilters_9);
      onChecked?.(false, column_4, nextFilters_9);
      setSearchFilterValue("");
    };
    $[188] = activeBuilderRow;
    $[189] = containsDraft;
    $[190] = draftFilterKind;
    $[191] = filterKind;
    $[192] = filters;
    $[193] = onChecked;
    $[194] = replaceFilterPreservingOrder;
    $[195] = setFilters;
    $[196] = setSearchFilterValue;
    $[197] = t54;
  } else {
    t54 = $[197];
  }
  const handleApplyContains = t54;
  let t55;
  if ($[198] !== activeBuilderRow || $[199] !== containsDraft || $[200] !== draftFilterKind || $[201] !== draftOperator || $[202] !== filterKind || $[203] !== filters) {
    bb3: {
      const column_5 = activeBuilderRow === "draft" ? draftFilterKind ?? filterKind : activeBuilderRow;
      if (!column_5) {
        t55 = null;
        break bb3;
      }
      let t56;
      if ($[205] !== column_5) {
        t56 = filter_9 => filter_9.column === column_5;
        $[205] = column_5;
        $[206] = t56;
      } else {
        t56 = $[206];
      }
      const rowFilter_2 = filters.find(t56);
      const operator_1 = draftOperator ?? (rowFilter_2 ? filterToOperator(rowFilter_2) : null);
      if (operator_1 !== "contains") {
        t55 = null;
        break bb3;
      }
      const committedText = rowFilter_2 && isContainsFilter(rowFilter_2) ? rowFilter_2.search?.trim() ?? "" : "";
      let t57;
      if ($[207] !== containsDraft) {
        t57 = containsDraft.trim();
        $[207] = containsDraft;
        $[208] = t57;
      } else {
        t57 = $[208];
      }
      const text_0 = t57;
      let t58;
      if ($[209] !== column_5 || $[210] !== committedText || $[211] !== text_0) {
        t58 = text_0 === committedText ? null : {
          column: column_5,
          text: text_0
        };
        $[209] = column_5;
        $[210] = committedText;
        $[211] = text_0;
        $[212] = t58;
      } else {
        t58 = $[212];
      }
      t55 = t58;
    }
    $[198] = activeBuilderRow;
    $[199] = containsDraft;
    $[200] = draftFilterKind;
    $[201] = draftOperator;
    $[202] = filterKind;
    $[203] = filters;
    $[204] = t55;
  } else {
    t55 = $[204];
  }
  const pendingContainsEdit = t55;
  let t56;
  bb4: {
    if (!pendingContainsEdit) {
      t56 = filters;
      break bb4;
    }
    let t57;
    if ($[213] !== pendingContainsEdit.column || $[214] !== pendingContainsEdit.text || $[215] !== replaceFilterPreservingOrder) {
      const nextFilter_6 = pendingContainsEdit.text ? buildGenericFilter({
        column: pendingContainsEdit.column,
        operator: "contains",
        values: [],
        containsText: pendingContainsEdit.text
      }) as T : null;
      t57 = replaceFilterPreservingOrder(pendingContainsEdit.column, nextFilter_6);
      $[213] = pendingContainsEdit.column;
      $[214] = pendingContainsEdit.text;
      $[215] = replaceFilterPreservingOrder;
      $[216] = t57;
    } else {
      t57 = $[216];
    }
    t56 = t57;
  }
  const filtersWithPendingContains = t56;
  let t57;
  if ($[217] !== allValuesSnapshot || $[218] !== filterKind || $[219] !== handleFilterChange || $[220] !== hasSelectedAll || $[221] !== targetFilterValues) {
    t57 = function handleSelectFilterValue(value, checked_0) {
      const baseValues = hasSelectedAll ? allValuesSnapshot : targetFilterValues as string[];
      const valuesSet = new Set(baseValues);
      if (checked_0) {
        valuesSet.add(value.value);
        if (filterKind) {
          filterUsageCollection.insert({
            id: crypto.randomUUID(),
            type: filterKind,
            timestamp: Date.now()
          });
        }
      } else {
        valuesSet.delete(value.value);
      }
      handleFilterChange(Array.from(valuesSet), false, hasSelectedAll ? false : undefined);
    };
    $[217] = allValuesSnapshot;
    $[218] = filterKind;
    $[219] = handleFilterChange;
    $[220] = hasSelectedAll;
    $[221] = targetFilterValues;
    $[222] = t57;
  } else {
    t57 = $[222];
  }
  const handleSelectFilterValue = t57;
  let t58;
  if ($[223] !== sortBy.direction) {
    t58 = by => {
      const newDirection = sortBy.direction === "asc" ? "desc" : "asc";
      setSortBy({
        by,
        direction: newDirection
      });
    };
    $[223] = sortBy.direction;
    $[224] = t58;
  } else {
    t58 = $[224];
  }
  const handleChangeSortByDirection = t58;
  let t59;
  if ($[225] === Symbol.for("react.memo_cache_sentinel")) {
    t59 = v => {
      const isAlpha = v === "alphabetical";
      const dir = isAlpha ? "asc" : "desc";
      setSortBy({
        by: v,
        direction: dir
      });
    };
    $[225] = t59;
  } else {
    t59 = $[225];
  }
  const handleChangeSortBy = t59;
  let t60;
  if ($[226] === Symbol.for("react.memo_cache_sentinel")) {
    t60 = (_column, kind_6, _currentValues) => {
      setDraftSpecialTimeKind(kind_6);
    };
    $[226] = t60;
  } else {
    t60 = $[226];
  }
  const handleSelectSpecialTimeKind = t60;
  let t61;
  if ($[227] !== filters || $[228] !== onChecked || $[229] !== replaceFilterPreservingOrder || $[230] !== setFilters || $[231] !== toSpecialTimeFilter) {
    t61 = (column_6, state) => {
      const values_4 = serializeSpecialTimeFilterState(state);
      if (!values_4) {
        return;
      }
      const existingFilter_4 = filters.find(filter_10 => filter_10.column === column_6);
      if (existingFilter_4 && JSON.stringify(existingFilter_4.values) === JSON.stringify(values_4)) {
        return;
      }
      const nextFilter_7 = toSpecialTimeFilter(column_6, values_4);
      const nextFilters_10 = replaceFilterPreservingOrder(column_6, nextFilter_7);
      setFilters(nextFilters_10);
      onChecked?.(false, column_6, nextFilters_10);
    };
    $[227] = filters;
    $[228] = onChecked;
    $[229] = replaceFilterPreservingOrder;
    $[230] = setFilters;
    $[231] = toSpecialTimeFilter;
    $[232] = t61;
  } else {
    t61 = $[232];
  }
  const handleCommitSpecialTimeState = t61;
  let t62;
  if ($[233] === Symbol.for("react.memo_cache_sentinel")) {
    t62 = (_column_0, comparator, _currentValues_0) => {
      setDraftInteractionSpecialComparator(comparator);
    };
    $[233] = t62;
  } else {
    t62 = $[233];
  }
  const handleSelectInteractionSpecialComparator = t62;
  let t63;
  if ($[234] !== filters || $[235] !== onChecked || $[236] !== replaceFilterPreservingOrder || $[237] !== setFilters || $[238] !== toInteractionSpecialFilter) {
    t63 = (column_7, state_0) => {
      const values_5 = serializeInteractionSpecialFilterState(state_0);
      if (!values_5) {
        return;
      }
      const existingFilter_5 = filters.find(filter_11 => filter_11.column === column_7);
      if (existingFilter_5 && JSON.stringify(existingFilter_5.values) === JSON.stringify(values_5)) {
        return;
      }
      const nextFilter_8 = toInteractionSpecialFilter(column_7, values_5);
      const nextFilters_11 = replaceFilterPreservingOrder(column_7, nextFilter_8);
      setFilters(nextFilters_11);
      onChecked?.(false, column_7, nextFilters_11);
    };
    $[234] = filters;
    $[235] = onChecked;
    $[236] = replaceFilterPreservingOrder;
    $[237] = setFilters;
    $[238] = toInteractionSpecialFilter;
    $[239] = t63;
  } else {
    t63 = $[239];
  }
  const handleCommitInteractionSpecialState = t63;
  let t64;
  if ($[240] !== filters || $[241] !== onChecked || $[242] !== replaceFilterPreservingOrder || $[243] !== setFilters || $[244] !== toUserMetricSpecialFilter) {
    t64 = (column_8, state_1) => {
      const values_6 = serializeUserMetricSpecialFilterState(state_1);
      if (!values_6) {
        return;
      }
      const existingFilter_6 = filters.find(filter_12 => filter_12.column === column_8);
      if (existingFilter_6 && JSON.stringify(existingFilter_6.values) === JSON.stringify(values_6)) {
        return;
      }
      const nextFilter_9 = toUserMetricSpecialFilter(column_8, values_6);
      const nextFilters_12 = replaceFilterPreservingOrder(column_8, nextFilter_9);
      setFilters(nextFilters_12);
      onChecked?.(false, column_8, nextFilters_12);
    };
    $[240] = filters;
    $[241] = onChecked;
    $[242] = replaceFilterPreservingOrder;
    $[243] = setFilters;
    $[244] = toUserMetricSpecialFilter;
    $[245] = t64;
  } else {
    t64 = $[245];
  }
  const handleCommitUserMetricSpecialState = t64;
  let t65;
  if ($[246] !== filters || $[247] !== onChecked || $[248] !== replaceFilterPreservingOrder || $[249] !== setFilters) {
    t65 = (column_9, state_2) => {
      const config = serializeLastInteractionTagFilter(state_2);
      if (!config) {
        return;
      }
      const existingFilter_7 = filters.find(filter_13 => filter_13.column === column_9);
      const nextFilter_10 = {
        ...(existingFilter_7 ?? {
          column: column_9
        }),
        ...config
      } as T;
      if (existingFilter_7 && existingFilter_7.column === nextFilter_10.column && (existingFilter_7 as GenericFilter & {
        tag?: string;
      }).tag === config.tag && JSON.stringify(existingFilter_7.values) === JSON.stringify(nextFilter_10.values) && existingFilter_7.is_exclude === nextFilter_10.is_exclude && existingFilter_7.select_all === nextFilter_10.select_all && existingFilter_7.search === nextFilter_10.search) {
        return;
      }
      const nextFilters_13 = replaceFilterPreservingOrder(column_9, nextFilter_10);
      setFilters(nextFilters_13);
      onChecked?.(false, column_9, nextFilters_13);
    };
    $[246] = filters;
    $[247] = onChecked;
    $[248] = replaceFilterPreservingOrder;
    $[249] = setFilters;
    $[250] = t65;
  } else {
    t65 = $[250];
  }
  const handleCommitLastInteractionTagState = t65;
  const builderFieldKinds = filteredAvailableFilterKinds;
  let t66;
  if ($[251] !== activateAppliedBuilderRow || $[252] !== activateDraftBuilderRow || $[253] !== activeBuilderRow || $[254] !== activeFieldType || $[255] !== aliasKindName || $[256] !== allowedOperators || $[257] !== availableFieldTypes || $[258] !== availableFilterValues || $[259] !== builderFieldKinds || $[260] !== builderPopoverRequest || $[261] !== canWrite || $[262] !== containsDraft || $[263] !== draftFilterKind || $[264] !== draftInteractionSpecialComparator || $[265] !== draftOperator || $[266] !== draftSpecialTimeKind || $[267] !== favoriteSavedFilterGroupsSet || $[268] !== favoriteTypesSet || $[269] !== filterKind || $[270] !== filterListPrepend || $[271] !== filterSearchFinal || $[272] !== filters || $[273] !== handleApplyContains || $[274] !== handleChangeSortByDirection || $[275] !== handleClearDraftRow || $[276] !== handleCommitInteractionSpecialState || $[277] !== handleCommitLastInteractionTagState || $[278] !== handleCommitSpecialTimeState || $[279] !== handleCommitUserMetricSpecialState || $[280] !== handleDeleteSavedFilterGroup || $[281] !== handleOnCheckSelectAll || $[282] !== handleRemoveAppliedFilterRow || $[283] !== handleSelectField || $[284] !== handleSelectFilterValue || $[285] !== handleSelectOperator || $[286] !== handleSelectSavedFilterGroup || $[287] !== hasSelectedAll || $[288] !== hideSavedFilters || $[289] !== invalidFilterColumns || $[290] !== isGlobal || $[291] !== isLoading || $[292] !== mappedProjectId || $[293] !== pendingDraftRow || $[294] !== popoverContainer || $[295] !== projectId || $[296] !== replaceExistingRowWithSpecialFilter || $[297] !== savedFilters || $[298] !== searchFilterValue || $[299] !== setFilterKind || $[300] !== setFilters || $[301] !== setSearchFilterValue || $[302] !== showSelectAll || $[303] !== sortBy || $[304] !== sortKindsWithFavoritesFirst || $[305] !== targetFilterValues || $[306] !== timeRange || $[307] !== volumeKind) {
    t66 = t67 => {
      const {
        rowFilter: rowFilter_3,
        isDraft: t68,
        conditionLabel: t69,
        showConditionConnector: t70
      } = t67;
      const isDraft = t68 === undefined ? false : t68;
      const conditionLabel = t69 === undefined ? null : t69;
      const showConditionConnector = t70 === undefined ? false : t70;
      const isActiveRow = isDraft ? activeBuilderRow === "draft" : activeBuilderRow === rowFilter_3?.column;
      const isEditingExistingRow = !isDraft && !!rowFilter_3 && isActiveRow;
      const draftRowState = isDraft ? isActiveRow || !pendingDraftRow ? {
        filterKind: draftFilterKind ?? filterKind,
        operator: draftOperator,
        containsText: containsDraft,
        specialTimeKind: draftSpecialTimeKind,
        interactionComparator: draftInteractionSpecialComparator
      } : pendingDraftRow : null;
      const rowKind = isDraft ? draftRowState?.filterKind ?? null : rowFilter_3?.column ?? null;
      const rowOperator = isDraft ? draftRowState?.operator ?? null : isEditingExistingRow && draftOperator ? draftOperator : rowFilter_3 ? filterToOperator(rowFilter_3) : null;
      const rowContainsText = isDraft ? draftRowState?.containsText ?? "" : isEditingExistingRow ? containsDraft : rowFilter_3?.search ?? "";
      const rowValues = (isDraft ? isActiveRow ? targetFilterValues : [] : rowFilter_3?.values ?? []) as string[];
      const valueSourceFilter = rowFilter_3 ?? (rowKind ? filters.find(f_10 => f_10.column === rowKind) : undefined);
      const valuesForLabel = (rowValues.length > 0 ? rowValues : valueSourceFilter?.values ?? []) as string[];
      const isSpecialTimeRow = isInteractionTimeFilter(rowKind);
      const isInteractionSpecialRow = isInteractionSpecialFilter(rowKind);
      const isUserMetricSpecialRow = isUserMetricSpecialFilter(rowKind);
      const isLastInteractionTagRow = isLastInteractionTagFilter(rowKind);
      const specialTimeValues = isSpecialTimeRow ? valueSourceFilter?.values : undefined;
      const interactionSpecialValues = isInteractionSpecialRow ? valueSourceFilter?.values : undefined;
      const userMetricSpecialValues = isUserMetricSpecialRow ? valueSourceFilter?.values : undefined;
      const specialTimeKind = isSpecialTimeRow ? (isDraft ? draftRowState?.specialTimeKind : isActiveRow && draftSpecialTimeKind) || getSpecialTimeKindFromValues(specialTimeValues) || InTheLastValueConfigKind.in_the_last : null;
      const interactionSpecialComparator = isInteractionSpecialRow ? (isDraft ? draftRowState?.interactionComparator : isActiveRow && draftInteractionSpecialComparator) || getInteractionSpecialComparatorFromValues(rowKind, interactionSpecialValues) || AboveValueConfigKind.above : null;
      const userMetricSpecialComparator = isUserMetricSpecialRow ? (isDraft ? draftRowState?.interactionComparator : isActiveRow && draftInteractionSpecialComparator) || getUserMetricComparatorFromValues(rowKind, userMetricSpecialValues) || AboveValueConfigKind.above : null;
      const baseSpecialTimeState = isSpecialTimeRow ? parseSpecialTimeFilterValues(specialTimeValues ?? createDefaultSpecialTimeFilterValues()) : undefined;
      const specialTimeState = isSpecialTimeRow ? isDraft || isActiveRow && draftSpecialTimeKind ? createSpecialTimeStateForKind(specialTimeKind ?? InTheLastValueConfigKind.in_the_last, baseSpecialTimeState) : baseSpecialTimeState : undefined;
      const baseInteractionSpecialState = isInteractionSpecialRow ? parseInteractionSpecialFilterValues(rowKind, interactionSpecialValues ?? createDefaultInteractionSpecialFilterValues(rowKind)) : undefined;
      const interactionSpecialState = isInteractionSpecialRow ? isDraft || isActiveRow && draftInteractionSpecialComparator ? createInteractionSpecialStateForComparator(rowKind, interactionSpecialComparator ?? AboveValueConfigKind.above, baseInteractionSpecialState) : baseInteractionSpecialState : undefined;
      const baseUserMetricSpecialState = isUserMetricSpecialRow ? parseUserMetricSpecialFilterValues(rowKind, userMetricSpecialValues ?? createDefaultUserMetricSpecialFilterValues(rowKind)) : undefined;
      const userMetricSpecialState = isUserMetricSpecialRow ? isDraft || isActiveRow && draftInteractionSpecialComparator ? createUserMetricSpecialStateForComparator(rowKind, userMetricSpecialComparator ?? AboveValueConfigKind.above, baseUserMetricSpecialState) : baseUserMetricSpecialState : undefined;
      const lastInteractionTagState = isLastInteractionTagRow ? parseLastInteractionTagFilter(valueSourceFilter as GenericFilter | undefined) : undefined;
      const rowValueLabel = isSpecialTimeRow ? formatSpecialTimePillSummary(specialTimeValues, {
        omitKind: true
      }) : isInteractionSpecialRow ? formatInteractionSpecialPillSummary(rowKind, interactionSpecialValues) : isUserMetricSpecialRow ? formatUserMetricSpecialPillSummary(rowKind, userMetricSpecialValues) : isLastInteractionTagRow ? formatLastInteractionTagPillSummary(valueSourceFilter as GenericFilter | undefined) : rowKind && valueSourceFilter && (rowOperator === "contains" ? isContainsFilter(valueSourceFilter) : valuesForLabel.length > 0 || !!valueSourceFilter.select_all) ? getFilterValueDisplayText(valueSourceFilter as T, valuesForLabel.map(value_0 => translate(rowKind as keyof typeof translate, value_0) === value_0 ? humanizeString(value_0) : translate(rowKind as keyof typeof translate, value_0)).join(", ")) : undefined;
      return <FilterContentV2Row key={isDraft ? "draft-filter-row" : rowFilter_3?.column} isActiveRow={isActiveRow} aliasKindName={aliasKindName} projectId={isGlobal ? mappedProjectId ?? projectId : projectId} filters={filters} timeRange={timeRange} conditionLabel={conditionLabel} showConditionConnector={showConditionConnector} popoverContainer={popoverContainer} fieldKind={rowKind} fieldLabel={rowKind ? buildFilterKindName(rowKind, aliasKindName) : undefined} operator={rowOperator} valueLabel={rowValueLabel} containsText={rowContainsText} onContainsTextChange={setContainsDraft} onApplyContains={handleApplyContains} onSelectField={kind_7 => {
        if (rowFilter_3 && rowFilter_3.column !== kind_7) {
          if (replaceExistingRowWithSpecialFilter(rowFilter_3, kind_7)) {
            return;
          }
          setFilters(filters.filter(f_11 => f_11.column !== rowFilter_3.column));
        } else {
          if (!rowFilter_3 && draftFilterKind && draftFilterKind !== kind_7) {
            setFilters(filters.filter(f_12 => f_12.column !== draftFilterKind));
          }
        }
        handleSelectField(kind_7);
      }} onSelectOperator={operator_2 => {
        if (rowFilter_3) {
          setActiveBuilderRow(rowFilter_3.column);
          setFilterKind(rowFilter_3.column);
          if (operator_2 === "contains") {
            setContainsDraft(isContainsFilter(rowFilter_3) ? rowFilter_3.search ?? "" : "");
          }
        } else {
          setActiveBuilderRow("draft");
        }
        handleSelectOperator(operator_2, rowFilter_3);
      }} onRemove={() => {
        if (rowFilter_3) {
          handleRemoveAppliedFilterRow(rowFilter_3);
          return;
        }
        handleClearDraftRow();
      }} removeDisabled={isDraft && filters.length === 0 && !rowKind && !rowOperator && !rowContainsText.trim()} fieldKinds={builderFieldKinds} favoriteTypes={favoriteTypesSet} onToggleFavorite={_temp15} filterSearch={filterSearchFinal} setFilterSearch={v_0 => setFilterSearchInternal(v_0.toLowerCase())} selectedFieldType={activeFieldType} availableFieldTypes={availableFieldTypes} onSelectFieldType={setActiveFieldType} fieldListPrepend={filterListPrepend} savedFilterGroups={hideSavedFilters ? [] : savedFilters} onSelectSavedFilterGroup={hideSavedFilters ? undefined : handleSelectSavedFilterGroup} onDeleteSavedFilterGroup={hideSavedFilters ? undefined : canWrite ? handleDeleteSavedFilterGroup : undefined} favoriteSavedFilterGroups={hideSavedFilters ? undefined : favoriteSavedFilterGroupsSet} onToggleFavoriteSavedFilterGroup={hideSavedFilters ? undefined : handleToggleFavoriteSavedFilterGroup} sortFavoritesFirst={sortKindsWithFavoritesFirst} searchFilterValue={searchFilterValue} setSearchFilterValue={setSearchFilterValue} sortBy={sortBy} onChangeSortBy={handleChangeSortBy} onChangeSortByDirection={handleChangeSortByDirection} availableFilterValues={isActiveRow && rowKind === filterKind ? availableFilterValues : undefined} isLoadingValues={isActiveRow && rowKind === filterKind ? isLoading : false} targetFilterValues={rowValues} hasSelectedAll={isActiveRow && rowKind === filterKind ? hasSelectedAll : !!rowFilter_3?.select_all} showSelectAll={isActiveRow && rowKind === filterKind ? showSelectAll : false} onCheckSelectAll={checked_1 => {
        if (rowFilter_3) {
          setFilterKind(rowFilter_3.column);
          setDraftOperator(filterToOperator(rowFilter_3));
          setContainsDraft("");
          if (checked_1) {
            setActiveBuilderRow(rowFilter_3.column);
          } else {
            setActiveBuilderRow("draft");
            setShowDraftRow(true);
          }
        } else {
          setActiveBuilderRow("draft");
        }
        handleOnCheckSelectAll(checked_1);
      }} onSelectFilterValue={(value_1, checked_2) => {
        if (rowFilter_3) {
          setActiveBuilderRow(rowFilter_3.column);
          setFilterKind(rowFilter_3.column);
          setDraftOperator(filterToOperator(rowFilter_3));
        } else {
          setActiveBuilderRow("draft");
        }
        handleSelectFilterValue(value_1, checked_2);
      }} volumeKind={volumeKind} onOpenField={() => {
        clearBuilderPopoverRequest();
        if (isDraft) {
          activateDraftBuilderRow();
          return;
        }
        if (!rowFilter_3) {
          return;
        }
        activateAppliedBuilderRow(rowFilter_3);
      }} onOpenOperator={() => {
        clearBuilderPopoverRequest();
        if (isDraft) {
          activateDraftBuilderRow();
          return;
        }
        if (rowFilter_3) {
          activateAppliedBuilderRow(rowFilter_3);
        }
      }} onOpenValue={() => {
        clearBuilderPopoverRequest();
        if (isDraft) {
          activateDraftBuilderRow();
        } else {
          if (rowFilter_3) {
            activateAppliedBuilderRow(rowFilter_3);
          }
        }
        setSearchFilterValue("");
      }} openPopoverRequest={isActiveRow ? builderPopoverRequest : undefined} isSpecialTimeFilter={isSpecialTimeRow} specialTimeKind={specialTimeKind} specialTimeState={specialTimeState} onSelectSpecialTimeKind={kind_9 => {
        if (!rowKind) {
          return;
        }
        if (rowFilter_3) {
          setActiveBuilderRow(rowFilter_3.column);
          setFilterKind(rowFilter_3.column);
        } else {
          setActiveBuilderRow("draft");
        }
        handleSelectSpecialTimeKind(rowKind, kind_9, specialTimeValues);
      }} onCommitSpecialTimeState={state_3 => {
        if (!rowKind) {
          return;
        }
        handleCommitSpecialTimeState(rowKind, state_3);
      }} isInteractionSpecialFilter={isInteractionSpecialRow} interactionSpecialComparatorKind={interactionSpecialComparator} interactionSpecialState={interactionSpecialState} onSelectInteractionSpecialComparator={comparator_0 => {
        if (!rowKind) {
          return;
        }
        if (rowFilter_3) {
          setActiveBuilderRow(rowFilter_3.column);
          setFilterKind(rowFilter_3.column);
        } else {
          setActiveBuilderRow("draft");
        }
        handleSelectInteractionSpecialComparator(rowKind, comparator_0, interactionSpecialValues);
      }} onCommitInteractionSpecialState={state_4 => {
        if (!rowKind) {
          return;
        }
        handleCommitInteractionSpecialState(rowKind, state_4);
      }} isUserMetricSpecialFilter={isUserMetricSpecialRow} userMetricSpecialComparatorKind={userMetricSpecialComparator} userMetricSpecialState={userMetricSpecialState} onSelectUserMetricSpecialComparator={comparator_1 => {
        if (!rowKind) {
          return;
        }
        if (rowFilter_3) {
          setActiveBuilderRow(rowFilter_3.column);
          setFilterKind(rowFilter_3.column);
        } else {
          setActiveBuilderRow("draft");
        }
        handleSelectInteractionSpecialComparator(rowKind, comparator_1, userMetricSpecialValues);
      }} onCommitUserMetricSpecialState={state_5 => {
        if (!rowKind) {
          return;
        }
        handleCommitUserMetricSpecialState(rowKind, state_5);
      }} isLastInteractionTagFilter={isLastInteractionTagRow} lastInteractionTagState={lastInteractionTagState} onCommitLastInteractionTagState={state_6 => {
        if (!rowKind) {
          return;
        }
        handleCommitLastInteractionTagState(rowKind, state_6);
      }} allowedOperators={allowedOperators} invalidFilterColumns={invalidFilterColumns} />;
    };
    $[251] = activateAppliedBuilderRow;
    $[252] = activateDraftBuilderRow;
    $[253] = activeBuilderRow;
    $[254] = activeFieldType;
    $[255] = aliasKindName;
    $[256] = allowedOperators;
    $[257] = availableFieldTypes;
    $[258] = availableFilterValues;
    $[259] = builderFieldKinds;
    $[260] = builderPopoverRequest;
    $[261] = canWrite;
    $[262] = containsDraft;
    $[263] = draftFilterKind;
    $[264] = draftInteractionSpecialComparator;
    $[265] = draftOperator;
    $[266] = draftSpecialTimeKind;
    $[267] = favoriteSavedFilterGroupsSet;
    $[268] = favoriteTypesSet;
    $[269] = filterKind;
    $[270] = filterListPrepend;
    $[271] = filterSearchFinal;
    $[272] = filters;
    $[273] = handleApplyContains;
    $[274] = handleChangeSortByDirection;
    $[275] = handleClearDraftRow;
    $[276] = handleCommitInteractionSpecialState;
    $[277] = handleCommitLastInteractionTagState;
    $[278] = handleCommitSpecialTimeState;
    $[279] = handleCommitUserMetricSpecialState;
    $[280] = handleDeleteSavedFilterGroup;
    $[281] = handleOnCheckSelectAll;
    $[282] = handleRemoveAppliedFilterRow;
    $[283] = handleSelectField;
    $[284] = handleSelectFilterValue;
    $[285] = handleSelectOperator;
    $[286] = handleSelectSavedFilterGroup;
    $[287] = hasSelectedAll;
    $[288] = hideSavedFilters;
    $[289] = invalidFilterColumns;
    $[290] = isGlobal;
    $[291] = isLoading;
    $[292] = mappedProjectId;
    $[293] = pendingDraftRow;
    $[294] = popoverContainer;
    $[295] = projectId;
    $[296] = replaceExistingRowWithSpecialFilter;
    $[297] = savedFilters;
    $[298] = searchFilterValue;
    $[299] = setFilterKind;
    $[300] = setFilters;
    $[301] = setSearchFilterValue;
    $[302] = showSelectAll;
    $[303] = sortBy;
    $[304] = sortKindsWithFavoritesFirst;
    $[305] = targetFilterValues;
    $[306] = timeRange;
    $[307] = volumeKind;
    $[308] = t66;
  } else {
    t66 = $[308];
  }
  const renderBuilderRow = t66;
  let t67;
  if ($[309] !== commitFilters || $[310] !== filtersWithPendingContains || $[311] !== isPopover || $[312] !== setOpen) {
    t67 = () => {
      const nextFilters_14 = filtersWithPendingContains.filter(_temp16);
      setIsDraftDirty(false);
      setDraftFilters(nextFilters_14);
      commitFilters(nextFilters_14);
      if (isPopover) {
        setOpen(false);
      }
    };
    $[309] = commitFilters;
    $[310] = filtersWithPendingContains;
    $[311] = isPopover;
    $[312] = setOpen;
    $[313] = t67;
  } else {
    t67 = $[313];
  }
  const handleApplyFilters = t67;
  let t68;
  if ($[314] !== draftActions || $[315] !== handleResetBuilderToDraft) {
    t68 = () => {
      if (!draftActions?.onReset) {
        return;
      }
      const restoredFilters = draftActions.onReset();
      setIsDraftDirty(false);
      if (restoredFilters) {
        setDraftFilters(restoredFilters as T[]);
      }
      handleResetBuilderToDraft((restoredFilters?.length ?? 0) === 0);
    };
    $[314] = draftActions;
    $[315] = handleResetBuilderToDraft;
    $[316] = t68;
  } else {
    t68 = $[316];
  }
  const handleResetReportDraft = t68;
  let t69;
  bb5: {
    if (!draftActions) {
      t69 = undefined;
      break bb5;
    }
    let t70;
    if ($[317] !== filtersWithPendingContains) {
      t70 = filtersWithPendingContains.some(_temp17);
      $[317] = filtersWithPendingContains;
      $[318] = t70;
    } else {
      t70 = $[318];
    }
    const hasDefinedFilters = t70;
    const blockedByEmptyDraft = !!draftActions.requireDefinedFilters && !hasDefinedFilters;
    const t71 = !!draftActions.canSaveForEveryone && !blockedByEmptyDraft;
    const t72 = blockedByEmptyDraft && draftActions.canSaveForEveryone ? draftActions.noDefinedFiltersMessage ?? draftActions.saveDisabledMessage : draftActions.saveDisabledMessage;
    let t73;
    if ($[319] !== draftActions || $[320] !== filtersWithPendingContains || $[321] !== isPopover || $[322] !== setOpen) {
      t73 = () => {
        const nextFilters_15 = filtersWithPendingContains.filter(_temp18);
        if (draftActions.appliesDraftOnSave === false) {
          draftActions.onSaveForEveryone(nextFilters_15);
          return;
        }
        setIsDraftDirty(false);
        setDraftFilters(nextFilters_15);
        draftActions.onSaveForEveryone(nextFilters_15);
        if (isPopover) {
          setOpen(false);
        }
      };
      $[319] = draftActions;
      $[320] = filtersWithPendingContains;
      $[321] = isPopover;
      $[322] = setOpen;
      $[323] = t73;
    } else {
      t73 = $[323];
    }
    let t74;
    if ($[324] !== draftActions || $[325] !== t71 || $[326] !== t72 || $[327] !== t73) {
      t74 = {
        ...draftActions,
        canSaveForEveryone: t71,
        saveDisabledMessage: t72,
        onSaveForEveryone: t73
      };
      $[324] = draftActions;
      $[325] = t71;
      $[326] = t72;
      $[327] = t73;
      $[328] = t74;
    } else {
      t74 = $[328];
    }
    t69 = t74;
  }
  const panelDraftActions = t69;
  let t70;
  if ($[329] !== activeBuilderRow || $[330] !== draftFilterKind || $[331] !== filterKind || $[332] !== filters || $[333] !== showDraftRow) {
    let t71;
    if ($[335] !== activeBuilderRow || $[336] !== draftFilterKind || $[337] !== filterKind || $[338] !== showDraftRow) {
      t71 = filter_17 => !(showDraftRow && activeBuilderRow === "draft" && (draftFilterKind ?? filterKind) && filter_17.column === (draftFilterKind ?? filterKind));
      $[335] = activeBuilderRow;
      $[336] = draftFilterKind;
      $[337] = filterKind;
      $[338] = showDraftRow;
      $[339] = t71;
    } else {
      t71 = $[339];
    }
    t70 = filters.filter(t71);
    $[329] = activeBuilderRow;
    $[330] = draftFilterKind;
    $[331] = filterKind;
    $[332] = filters;
    $[333] = showDraftRow;
    $[334] = t70;
  } else {
    t70 = $[334];
  }
  const visibleFilters = t70;
  const showDraftBuilderRow = showDraftRow || !filters.length;
  const totalVisibleRows = visibleFilters.length + (showDraftBuilderRow ? 1 : 0);
  const showConditionLabels = totalVisibleRows >= 2;
  const showReset = !!draftActions?.onReset && !!showDraftActions;
  let t71;
  if ($[340] !== className) {
    t71 = cn("flex h-full w-full flex-col", className);
    $[340] = className;
    $[341] = t71;
  } else {
    t71 = $[341];
  }
  let t72;
  if ($[342] !== autoApply || $[343] !== canWrite || $[344] !== draftActions || $[345] !== filters.length || $[346] !== handleApplyFilters || $[347] !== handleNewFilterRow || $[348] !== handleResetReportDraft || $[349] !== handleSaveFilters || $[350] !== hideSaveButton || $[351] !== isDraftDirty || $[352] !== panelDraftActions || $[353] !== pendingContainsEdit || $[354] !== renderBuilderRow || $[355] !== showConditionLabels || $[356] !== showDraftActions || $[357] !== showDraftBuilderRow || $[358] !== showReset || $[359] !== totalVisibleRows || $[360] !== visibleFilters) {
    t72 = true && <FilterContentV2Panel onNewFilter={handleNewFilterRow} onSave={handleSaveFilters} showSave={!hideSaveButton} saveDisabled={!filters.length || !canWrite} saveTooltip={!canWrite ? SAVE_FILTERS_REQUIRES_WRITE_MESSAGE : undefined} showReset={showReset} onReset={handleResetReportDraft} onApply={handleApplyFilters} applyDisabled={!isDraftDirty && !pendingContainsEdit} showApply={!autoApply} showDraftActions={!!draftActions && (showDraftActions || isDraftDirty || !!pendingContainsEdit)} draftActions={panelDraftActions}>{visibleFilters.map((filter_18, index) => renderBuilderRow({
        rowFilter: filter_18,
        conditionLabel: showConditionLabels ? index === 0 ? "IF" : "AND" : null,
        showConditionConnector: showConditionLabels && index < totalVisibleRows - 1
      }))}{showDraftBuilderRow && renderBuilderRow({
        isDraft: true,
        conditionLabel: showConditionLabels ? visibleFilters.length === 0 ? "IF" : "AND" : null,
        showConditionConnector: false
      })}</FilterContentV2Panel>;
    $[342] = autoApply;
    $[343] = canWrite;
    $[344] = draftActions;
    $[345] = filters.length;
    $[346] = handleApplyFilters;
    $[347] = handleNewFilterRow;
    $[348] = handleResetReportDraft;
    $[349] = handleSaveFilters;
    $[350] = hideSaveButton;
    $[351] = isDraftDirty;
    $[352] = panelDraftActions;
    $[353] = pendingContainsEdit;
    $[354] = renderBuilderRow;
    $[355] = showConditionLabels;
    $[356] = showDraftActions;
    $[357] = showDraftBuilderRow;
    $[358] = showReset;
    $[359] = totalVisibleRows;
    $[360] = visibleFilters;
    $[361] = t72;
  } else {
    t72 = $[361];
  }
  let t73;
  if ($[362] !== t71 || $[363] !== t72) {
    t73 = <div className={t71}>{t72}</div>;
    $[362] = t71;
    $[363] = t72;
    $[364] = t73;
  } else {
    t73 = $[364];
  }
  return t73;
};
function _temp(q) {
  return q.from({
    favorites: favoriteFiltersCollection
  });
}
function _temp2(q_0) {
  return q_0.from({
    favorites: favoriteSavedFiltersCollection
  });
}
function _temp3(t) {
  return {
    type: t.usage.type,
    count: count(t.usage)
  };
}
function _temp4(t_0) {
  return t_0.usage.type;
}
function _temp5(t_1) {
  return count(t_1.usage);
}
function _temp6(q_1) {
  return q_1.from({
    usage: filterUsageCollection
  }).select(_temp3).groupBy(_temp4).orderBy(_temp5, "desc");
}
function _temp7(filter) {
  return filter.type;
}
function _temp8(filter_0) {
  return filter_0.type;
}
function _temp9(acc, filter_1) {
  const valueEntries = isContainsFilter(filter_1) ? [filter_1.search?.trim() ?? ""] : filter_1.column === FILTERS.lastInteractionTag || !Array.isArray(filter_1.values) ? [filterTextValue(filter_1)].filter(Boolean) : filter_1.values;
  if (acc[filter_1.column]) {
    acc[filter_1.column].push(...valueEntries);
  } else {
    acc[filter_1.column] = [...valueEntries];
  }
  return acc;
}
function _temp10(a) {
  return a.value;
}
function _temp11(filter_8) {
  return {
    column: filter_8.column,
    values: filter_8.values,
    is_exclude: filter_8.is_excluded,
    search: filter_8.search
  };
}
function _temp12(f_7) {
  return f_7.type;
}
function _temp13(filterGroup_1, isFavorite) {
  const key = savedFilterGroupKey(filterGroup_1);
  if (isFavorite) {
    favoriteSavedFiltersCollection.delete(key);
  } else {
    favoriteSavedFiltersCollection.insert({
      type: key
    });
  }
}
function _temp14(f_8) {
  return f_8.type;
}
function _temp15(kind_8, isFavorite_0) {
  if (isFavorite_0) {
    favoriteFiltersCollection.delete(kind_8);
  } else {
    favoriteFiltersCollection.insert({
      type: kind_8
    });
  }
}
function _temp16(filter_14) {
  return isFilterDefined(filter_14);
}
function _temp17(filter_15) {
  return isFilterDefined(filter_15);
}
function _temp18(filter_16) {
  return isFilterDefined(filter_16);
}